/**
 * Layer 2 — ReferenceData
 * ─────────────────────────────────────────────────────────────────────────────
 * Version-controlled lookup / mapping tables that will later be migrated from
 * the validated Life Blueprint V2.9.9 Excel DB sheets.
 *
 * RULES
 *  - Reference data must be MIGRATED from the Golden Master, never generated,
 *    inferred, or edited by AI.
 *  - Every reference table carries a `_meta` block declaring its source and
 *    version. The rest of the app must NEVER read a table without inspecting
 *    `_meta`.
 *  - Table shapes intentionally follow "list of records with a stable id
 *    column" so an Excel row → JS object mapping is 1:1 and diffable.
 *  - MOCK tables here contain no real numbers, no interpretations, and no
 *    weights. They are structurally-valid empty shells so downstream layers
 *    can compile and be tested.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { SOURCE_STATUS } from "./sourceStatus";

/**
 * @typedef {Object} RefTableMeta
 * @property {string} tableId              stable id, e.g. "R-TIME-SLOT-CODES"
 * @property {"MOCK_POC"|"VALIDATED_V299"} source
 * @property {string} version              e.g. "POC-EMPTY" or "V2.9.9-2026-02"
 * @property {?string} goldenMasterSheet   e.g. "DB_TimeSlots" (once known)
 * @property {?string} note
 */

/**
 * @typedef {Object} ReferenceTable
 * @property {RefTableMeta} _meta
 * @property {Object[]} rows
 */

/**
 * The list of reference tables that CoreRuleEngine / AnalysisEngine /
 * InterpretationEngine will require. Each table is registered with a table id
 * so downstream code fetches by id (never by ad-hoc key), enabling a clean
 * later swap from MOCK_POC to VALIDATED_V299.
 *
 * NOTE: All rows are intentionally empty until the Golden Master supplies them.
 * MOCK layers below do NOT read these tables; they read `src/lib/mockProfiles`
 * which is clearly separated from production reference data.
 */
const EMPTY_TABLE = (tableId, sheet, note) => ({
  _meta: {
    tableId,
    source: SOURCE_STATUS.MOCK_POC,
    version: "POC-EMPTY",
    goldenMasterSheet: sheet || null,
    note: note || "Awaiting migration from V2.9.9 Golden Master.",
  },
  rows: [],
});

const TABLES = {
  // Core rule inputs (BZ / Sheet27 dependencies)
  "R-TIME-SLOT-CODES":      EMPTY_TABLE("R-TIME-SLOT-CODES",      "DB_TimeSlots",      "Maps 时辰 → internal code required by CoreRuleEngine."),
  "R-GENDER-CODES":         EMPTY_TABLE("R-GENDER-CODES",         "DB_Gender",         "Maps raw gender → V2.9.9 gender code."),
  "R-REGION-CODES":         EMPTY_TABLE("R-REGION-CODES",         "DB_Regions",        "Maps region label → V2.9.9 region code (if used)."),
  "R-CALENDAR-CONVERSION":  EMPTY_TABLE("R-CALENDAR-CONVERSION",  "DB_Calendar",      "Solar/lunar conversion table if required by V2.9.9."),

  // Analysis lookups (Sheet28)
  "R-ANALYSIS-LOOKUPS":     EMPTY_TABLE("R-ANALYSIS-LOOKUPS",     "Sheet28_Lookups",   "Aggregation / scoring lookup rows used by AnalysisEngine."),
  "R-ANALYSIS-THRESHOLDS":  EMPTY_TABLE("R-ANALYSIS-THRESHOLDS",  "Sheet28_Thresholds","Score bands and conditional thresholds."),

  // Interpretation rules (Sheet29)
  "R-INTERPRETATION-RULES": EMPTY_TABLE("R-INTERPRETATION-RULES", "Sheet29_Rules",     "Deterministic combination/interpretation rules. No AI allowed."),
  "R-INTERPRETATION-TEXT":  EMPTY_TABLE("R-INTERPRETATION-TEXT",  "Sheet29_Text",      "Frozen text fragments emitted by interpretation rules."),

  // Report shaping (Sheet30)
  "R-REPORT-LAYOUT":        EMPTY_TABLE("R-REPORT-LAYOUT",        "Sheet30_Layout",    "Which analysis fields map to which UI cards / sections."),

  // 12 themes + 24 numbers taxonomies
  "R-THEMES-12-TAXONOMY":   EMPTY_TABLE("R-THEMES-12-TAXONOMY",   "DB_Themes",         "Official 12-theme names, order and semantics."),
  "R-NUMBERS-24-TAXONOMY":  EMPTY_TABLE("R-NUMBERS-24-TAXONOMY",  "DB_Numbers",        "Official 24-number classification and usage rules."),
};

/**
 * Frozen reference-data bundle passed as a parameter into every layer.
 * Downstream layers must NEVER mutate this bundle.
 */
export const referenceData = Object.freeze({
  version: "POC-EMPTY",
  source: SOURCE_STATUS.MOCK_POC,
  tables: Object.freeze(TABLES),
  /**
   * Strict getter. Throws if the requested table id is unknown so typos are
   * caught at development time.
   */
  getTable(tableId) {
    const t = TABLES[tableId];
    if (!t) throw new Error(`[ReferenceData] Unknown table id: ${tableId}`);
    return t;
  },
});

/**
 * Enumerate all registered table ids. Useful for the validation harness and
 * for later migration progress reporting.
 */
export function listReferenceTableIds() {
  return Object.keys(TABLES);
}
